import {
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { translate, useLocaleOrDefault } from '../../../shared/i18n/index.js';
import './DataGrid.css';

/**
 * M01-§5 shared data-grid. Wraps a plain list in sort / per-column filter /
 * global search / pagination / column-resize / CSV export / numeric totals.
 * Purely presentational — the host page still owns data fetching, loading and
 * error UI. Renders `<table aria-label={caption}>` so existing table queries
 * keep working; rows/cells/actions are produced by the caller via `columns`,
 * `rowTestId` and `actions`, preserving each screen's data-testids.
 */

export interface DataGridColumn<T> {
  /** Stable identifier, used for sort/filter/resize keys. */
  readonly key: string;
  /** Column header label. */
  readonly header: string;
  /** Scalar accessor for sort / filter / search / export / totals. */
  readonly value?: (row: T) => string | number;
  /** Custom cell renderer; falls back to `value` when omitted. */
  readonly render?: (row: T) => ReactNode;
  /** Allow sorting on this column (default true when `value` given). */
  readonly sortable?: boolean;
  /** Allow filtering on this column (default true when `value` given). */
  readonly filterable?: boolean;
  /** Treat as numeric for sorting + totals. */
  readonly numeric?: boolean;
}

export interface DataGridProps<T> {
  /** Table accessible name (aria-label). */
  readonly caption: string;
  readonly columns: readonly DataGridColumn<T>[];
  readonly rows: readonly T[];
  readonly getRowId: (row: T) => string;
  /** Optional per-row data-testid (e.g. `row-${id}`). */
  readonly rowTestId?: (row: T) => string;
  /** Optional per-row aria-label (e.g. `row-${id}`), for label-based row queries. */
  readonly rowAriaLabel?: (row: T) => string;
  /** Optional trailing actions cell (Edit / Deactivate …). */
  readonly actions?: (row: T) => ReactNode;
  readonly actionsHeader?: string;
  readonly pageSize?: number;
  readonly searchPlaceholder?: string;
  /** Optional accessible name for the global search box (default `${caption}-search`). */
  readonly searchAriaLabel?: string;
  readonly exportFileName?: string;
  readonly showTotals?: boolean;
  readonly emptyMessage?: string;
}

type SortState = { readonly key: string; readonly dir: 'asc' | 'desc' } | null;

const PAGE_SIZES = [10, 25, 50, 100] as const;

function csvCell(raw: string): string {
  return /[",\r\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
}

function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  let href: string;
  let revoke = false;
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    href = URL.createObjectURL(blob);
    revoke = true;
  } else {
    href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  }
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (revoke) URL.revokeObjectURL(href);
}

export function DataGrid<T>({
  caption,
  columns,
  rows,
  getRowId,
  rowTestId,
  rowAriaLabel,
  actions,
  actionsHeader,
  pageSize: initialPageSize = 10,
  searchPlaceholder,
  searchAriaLabel,
  exportFileName,
  showTotals = false,
  emptyMessage,
}: DataGridProps<T>): ReactNode {
  // Works with or without an I18nProvider above it, like DateInput.
  const locale = useLocaleOrDefault();
  const tr = (key: string, vars?: Readonly<Record<string, string | number>>) => translate(locale, `grid.${key}`, vars);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const drag = useRef<{ key: string; startX: number; startW: number } | null>(null);

  const searchable = useMemo(
    () => columns.filter((c) => c.value),
    [columns],
  );

  const filtered = useMemo(() => {
    let out = rows.slice();
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        searchable.some((c) => String(c.value!(r)).toLowerCase().includes(q)),
      );
    }
    for (const c of columns) {
      if (!c.value) continue;
      const fv = (filters[c.key] ?? '').trim().toLowerCase();
      if (fv) out = out.filter((r) => String(c.value!(r)).toLowerCase().includes(fv));
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col?.value) {
        const acc = col.value;
        out.sort((a, b) => {
          const av = acc(a);
          const bv = acc(b);
          const cmp = col.numeric
            ? Number(av) - Number(bv)
            : String(av).localeCompare(String(bv));
          return sort.dir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return out;
  }, [rows, search, filters, sort, columns, searchable]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const start = (current - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  const toggleSort = (key: string) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const onFilterChange = (key: string, v: string) => {
    setFilters((f) => ({ ...f, [key]: v }));
    setPage(1);
  };

  const onSearchChange = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const startResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const startW = widths[key] ?? 120;
    drag.current = { key, startX: e.clientX, startW };
    const move = (ev: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      const next = Math.max(48, d.startW + (ev.clientX - d.startX));
      setWidths((w) => ({ ...w, [d.key]: next }));
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const onExport = () => {
    const cols = columns.filter((c) => c.value);
    const header = cols.map((c) => csvCell(c.header)).join(',');
    const lines = filtered.map((r) =>
      cols.map((c) => csvCell(String(c.value!(r)))).join(','),
    );
    downloadCsv(exportFileName ?? `${caption}.csv`, [header, ...lines].join('\r\n'));
  };

  const numericCols = showTotals ? columns.filter((c) => c.numeric && c.value) : [];
  const totalsByKey = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const c of numericCols) {
      acc[c.key] = filtered.reduce((sum, r) => sum + Number(c.value!(r)), 0);
    }
    return acc;
  }, [filtered, numericCols]);

  const colSpan = columns.length + (actions ? 1 : 0);

  return (
    <div className="datagrid" data-testid={`grid-${caption}`}>
      <div className="datagrid-toolbar">
        <input
          type="search"
          data-testid="grid-search"
          aria-label={searchAriaLabel ?? `${caption}-search`}
          placeholder={searchPlaceholder ?? tr('search')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <button
          type="button"
          className="datagrid-btn"
          data-testid="grid-filter-toggle"
          aria-pressed={showFilters}
          onClick={() => setShowFilters((v) => !v)}
        >
          {tr('filters')}
        </button>
        <button type="button" className="datagrid-btn" data-testid="grid-export" onClick={onExport}>
          {tr('export')}
        </button>
      </div>

      <table aria-label={caption}>
        <colgroup>
          {columns.map((c) => (
            <col
              key={c.key}
              data-testid={`col-${c.key}`}
              style={{ width: widths[c.key] ? `${widths[c.key]}px` : undefined }}
            />
          ))}
          {actions ? <col data-testid="col-actions" /> : null}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => {
              const canSort = c.sortable !== false && !!c.value;
              const dir = sort?.key === c.key ? sort.dir : null;
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={c.numeric ? 'datagrid-num' : undefined}
                  aria-sort={canSort ? (dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none') : undefined}
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="datagrid-sort"
                      data-testid={`sort-${c.key}`}
                      onClick={() => toggleSort(c.key)}
                    >
                      {c.header}
                      {dir === 'asc' ? ' ▲' : dir === 'desc' ? ' ▼' : ''}
                    </button>
                  ) : (
                    c.header
                  )}
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    data-testid={`resize-${c.key}`}
                    className="datagrid-resize"
                    onMouseDown={(e) => startResize(e, c.key)}
                  />
                </th>
              );
            })}
            {actions ? <th scope="col">{actionsHeader ?? tr('actions')}</th> : null}
          </tr>
          {showFilters ? (
            <tr data-testid="grid-filter-row">
              {columns.map((c) => (
                <th key={c.key} scope="col">
                  {c.value && c.filterable !== false ? (
                    <input
                      data-testid={`filter-${c.key}`}
                      aria-label={tr('filterBy', { column: c.header })}
                      value={filters[c.key] ?? ''}
                      onChange={(e) => onFilterChange(c.key, e.target.value)}
                    />
                  ) : null}
                </th>
              ))}
              {actions ? <th /> : null}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {pageRows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} data-testid="grid-empty">
                {emptyMessage ?? tr('empty')}
              </td>
            </tr>
          ) : (
            pageRows.map((r) => {
              const tid = rowTestId?.(r);
              const aria = rowAriaLabel?.(r);
              return (
                <tr
                  key={getRowId(r)}
                  {...(tid ? { 'data-testid': tid } : {})}
                  {...(aria ? { 'aria-label': aria } : {})}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? 'datagrid-num' : undefined}>
                      {c.render ? c.render(r) : c.value ? String(c.value(r)) : null}
                    </td>
                  ))}
                  {actions ? <td>{actions(r)}</td> : null}
                </tr>
              );
            })
          )}
        </tbody>
        {showTotals && numericCols.length > 0 ? (
          <tfoot>
            <tr data-testid="grid-totals">
              {columns.map((c, i) => (
                <td key={c.key} className={c.numeric ? 'datagrid-num' : undefined}>
                  {c.numeric && c.value
                    ? totalsByKey[c.key]
                    : i === 0
                      ? tr('total')
                      : null}
                </td>
              ))}
              {actions ? <td /> : null}
            </tr>
          </tfoot>
        ) : null}
      </table>

      <div className="datagrid-pager">
        <button
          type="button"
          className="datagrid-btn"
          data-testid="grid-prev"
          disabled={current <= 1}
          onClick={() => setPage(current - 1)}
        >
          {tr('prev')}
        </button>
        <span data-testid="grid-page-info">
          {tr('pageOf', { page: current, pages: pageCount })}
        </span>
        <button
          type="button"
          className="datagrid-btn"
          data-testid="grid-next"
          disabled={current >= pageCount}
          onClick={() => setPage(current + 1)}
        >
          {tr('next')}
        </button>
        <label className="datagrid-pagesize">
          {tr('rowsPerPage')}
          <select
            data-testid="grid-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <span data-testid="grid-count">{tr('rowCount', { count: total })}</span>
      </div>
    </div>
  );
}
